/**
 * KernelDetail — Tabbed detail view for a single Kernel Strategy
 *
 * Tabs:
 *   1. Kernel      — Diagnosis, Guiding Policy, Coherent Actions (KernelForm)
 *   2. Challenges  — ASC Scatter Plot + Crux selection (AscMatrix)
 *   3. Objectives  — Proximate Objectives cascade (ProximateObjectivesTree)
 *   4. Takes       — Responsibility Grid (TakeGrid)
 *   5. Assumptions — Strategic Navigation Log (AssumptionTracker)
 *
 * Also handles creation when :kernelId === "new".
 * Route: /prioritization/strategy/kernel/:kernelId
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  ArchiveBoxIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { useStrategyKernelStore } from '../../../../stores/prioritization/strategyKernelStore';
import { KernelForm } from '../../../../components/prioritization/strategyKernel/KernelForm';
import { AscMatrix } from '../../../../components/prioritization/strategyKernel/AscMatrix';
import { ProximateObjectivesTree } from '../../../../components/prioritization/strategyKernel/ProximateObjectivesTree';
import { TakeGrid } from '../../../../components/prioritization/strategyKernel/TakeGrid';
import { AssumptionTracker } from '../../../../components/prioritization/strategyKernel/AssumptionTracker';
import type { KernelTimeHorizon } from '../../../../types/prioritization/strategyKernel';
import { getKernelStatusLabel, getKernelTimeHorizonLabel } from '../../../../types/prioritization/strategyKernel';
import { StrategyConnectivityHelpButton } from '../StrategyConnectivityDocModal';

// ============================================================================
// Tab definitions
// ============================================================================

type Tab = 'kernel' | 'challenges' | 'objectives' | 'takes' | 'assumptions';

const TABS: Array<{ id: Tab; label: string; description: string }> = [
  { id: 'kernel',      label: 'Kernel',       description: 'Diagnosis · Guiding Policy · Coherent Actions' },
  { id: 'challenges',  label: 'Challenges',   description: 'ASC matrix — find the Crux' },
  { id: 'objectives',  label: 'Objectives',   description: 'Proximate objective cascade' },
  { id: 'takes',       label: 'Takes',        description: 'Team responsibility grid' },
  { id: 'assumptions', label: 'Assumptions',  description: 'Strategic navigation log' },
];

// ============================================================================
// Challenge list panel (used in the Challenges tab)
// ============================================================================

function ChallengesTab({ kernelId }: { kernelId: string }) {
  const { getChallengesForKernel, createChallenge, setCrux, clearCrux } =
    useStrategyKernelStore();

  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newImportance, setNewImportance] = useState(5);
  const [newAddressability, setNewAddressability] = useState(5);

  const challenges = getChallengesForKernel(kernelId);

  const handleAdd = useCallback(async () => {
    if (!newTitle.trim()) return;
    await createChallenge({
      kernelId,
      title: newTitle.trim(),
      description: newDescription.trim() || undefined,
      importance: newImportance,
      addressability: newAddressability,
    });
    setNewTitle('');
    setNewDescription('');
    setNewImportance(5);
    setNewAddressability(5);
    setAdding(false);
  }, [kernelId, newTitle, newDescription, newImportance, newAddressability, createChallenge]);

  const handleSelectCrux = useCallback((challengeId: string) => {
    setCrux(kernelId, challengeId);
  }, [kernelId, setCrux]);

  const handleClearCrux = useCallback(() => {
    clearCrux(kernelId);
  }, [kernelId, clearCrux]);

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="text-sm text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3">
        <p className="font-medium text-[var(--text-ter)] mb-1">Addressable Strategic Challenges (ASC) Matrix</p>
        <p>List the challenges your organization faces. Rate each on <strong>Importance</strong> (how much it threatens the core or represents an opportunity) and <strong>Addressability</strong> (how solvable it is). The top-right quadrant contains your candidates for the <strong>Crux</strong> — the keystone challenge that, if broken, unlocks the most forward movement.</p>
      </div>

      {/* Matrix */}
      <AscMatrix
        challenges={challenges}
        onSelectCrux={handleSelectCrux}
        onClearCrux={handleClearCrux}
      />

      {/* Add challenge form */}
      {adding ? (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-3">
          <h4 className="text-sm font-medium text-[var(--text-ter)]">Add challenge</h4>
          <input
            type="text"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Challenge title"
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600"
            autoFocus
          />
          <textarea
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 outline-none focus:ring-1 focus:ring-amber-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600 resize-none"
          />
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs text-[var(--text-ter)] mb-1">Importance (1–10)</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={newImportance}
                  onChange={e => setNewImportance(Number(e.target.value))}
                  className="flex-1 accent-amber-500"
                />
                <span className="text-sm font-mono w-5 text-center text-[var(--text-ter)]">{newImportance}</span>
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-[var(--text-ter)] mb-1">Addressability (1–10)</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={newAddressability}
                  onChange={e => setNewAddressability(Number(e.target.value))}
                  className="flex-1 accent-amber-500"
                />
                <span className="text-sm font-mono w-5 text-center text-[var(--text-ter)]">{newAddressability}</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="text-sm text-[var(--text-ter)] px-3 py-1.5 hover:text-[var(--text-ter)]">
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!newTitle.trim()}
              className="text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-1.5"
            >
              Add challenge
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--mustard)] hover:text-[var(--mustard)] hover:bg-[var(--mustard)]/20 px-3 py-1.5 border border-[var(--mustard)] transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add challenge
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Creation form (when kernelId === "new")
// ============================================================================

type KernelNewLocationState = {
  parentStrategyId?: string;
  prefillName?: string;
  prefillDescription?: string;
  prefillTimeHorizon?: KernelTimeHorizon;
};

function CreateKernelForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { createKernel } = useStrategyKernelStore();

  const fromStrategy = (location.state ?? null) as KernelNewLocationState | null;

  const [name, setName] = useState(fromStrategy?.prefillName ?? '');
  const [description, setDescription] = useState(fromStrategy?.prefillDescription ?? '');
  const [timeHorizon, setTimeHorizon] = useState<KernelTimeHorizon>(
    fromStrategy?.prefillTimeHorizon ?? 'annual'
  );

  const handleCreate = useCallback(async () => {
    if (!name.trim()) return;
    const id = await createKernel({ name: name.trim(), description: description.trim() || undefined, timeHorizon });
    navigate(`/prioritization/strategy/kernel/${id}`);
  }, [name, description, timeHorizon, createKernel, navigate]);

  return (
    <div className="max-w-xl mx-auto mt-10 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/prioritization/strategy/kernel" className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
          <ArrowLeftIcon className="w-4 h-4" />
        </Link>
        <h2 className="text-lg font-semibold text-[var(--text-pri)]">New Kernel Strategy</h2>
      </div>

      {fromStrategy?.parentStrategyId && (
        <p className="text-xs text-[var(--text-ter)] px-1">
          Classic strategy record saved — tune the kernel name or continue to Rumelt fields.
        </p>
      )}

      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-[var(--text-ter)] mb-1">Strategy name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. FY2026 Market Expansion Strategy"
            className="w-full text-sm border border-[var(--rule)] px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-amber-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-ter)] mb-1">Description <span className="font-normal text-[var(--text-ter)]">(optional)</span></label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Brief context about what this strategy addresses"
            rows={3}
            className="w-full text-sm border border-[var(--rule)] px-3.5 py-2.5 outline-none focus:ring-1 focus:ring-amber-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600 resize-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-ter)] mb-1">Time horizon</label>
          <select
            value={timeHorizon}
            onChange={e => setTimeHorizon(e.target.value as KernelTimeHorizon)}
            className="text-sm border border-[var(--rule)] px-3 py-2 outline-none focus:ring-1 focus:ring-amber-500 bg-[var(--bg-raised)] text-[var(--text-ter)]"
          >
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="multi-year">Multi-Year</option>
          </select>
        </div>
        <p className="text-xs text-[var(--text-ter)]">
          After creating, you'll define the <strong className="text-[var(--text-ter)]">Diagnosis</strong>, <strong className="text-[var(--text-ter)]">Guiding Policy</strong>, and <strong className="text-[var(--text-ter)]">Coherent Actions</strong> in the Kernel tab.
        </p>
        <div className="flex justify-end gap-2">
          <Link
            to="/prioritization/strategy/kernel"
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] px-3 py-2"
          >
            Cancel
          </Link>
          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            className="text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2"
          >
            Create kernel strategy
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main KernelDetail
// ============================================================================

export function KernelDetail() {
  const { kernelId } = useParams<{ kernelId: string }>();
  const navigate = useNavigate();

  const {
    getKernel,
    fetchFromSupabase,
    updateKernel,
    deleteKernel,
    activateKernel,
    archiveKernel,
    isLoading,
  } = useStrategyKernelStore();

  const [activeTab, setActiveTab] = useState<Tab>('kernel');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);

  // Handle creation mode
  if (kernelId === 'new') {
    return <CreateKernelForm />;
  }

  const kernel = kernelId ? getKernel(kernelId) : undefined;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-[var(--text-ter)]">
        Loading…
      </div>
    );
  }

  if (!kernel) {
    return (
      <div className="text-center mt-16 space-y-3">
        <p className="text-[var(--text-ter)]">Strategy not found.</p>
        <Link to="/prioritization/strategy/kernel" className="text-sm text-[var(--text-ter)] hover:underline">
          Back to Kernel Strategies
        </Link>
      </div>
    );
  }

  const handleSaveName = async () => {
    if (nameInput.trim() && nameInput !== kernel.name) {
      await updateKernel(kernel.id, { name: nameInput.trim() });
    }
    setEditingName(false);
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(`Delete "${kernel.name}"? This cannot be undone.`);
    if (!confirmed) return;
    await deleteKernel(kernel.id);
    navigate('/prioritization/strategy/kernel');
  };

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-start gap-4">
        <Link
          to="/prioritization/strategy/kernel"
          className="mt-1 text-[var(--text-ter)] hover:text-[var(--text-ter)] shrink-0"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </Link>

        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onBlur={handleSaveName}
                onKeyDown={e => e.key === 'Enter' && handleSaveName()}
                className="text-xl font-semibold text-[var(--text-pri)] border-b-2 border-[var(--mustard)] bg-transparent outline-none flex-1"
                autoFocus
              />
              <button onClick={handleSaveName} className="text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]">
                <CheckIcon className="w-4 h-4" />
              </button>
              <button onClick={() => setEditingName(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[var(--text-pri)] truncate">{kernel.name}</h1>
              <button
                onClick={() => { setNameInput(kernel.name); setEditingName(true); }}
                className="text-[var(--text-sec)] hover:text-[var(--text-ter)] shrink-0"
              >
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              kernel.status === 'active'   ? 'bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]' :
              kernel.status === 'draft'    ? 'bg-[var(--bg-raised)] text-[var(--text-ter)]'  :
                                             'bg-[var(--coral)]/40 text-[var(--coral)]'
            }`}>
              {getKernelStatusLabel(kernel.status)}
            </span>
            <span className="text-xs text-[var(--text-ter)]">
              {getKernelTimeHorizonLabel(kernel.timeHorizon)}
            </span>
            {kernel.description && (
              <span className="text-xs text-[var(--text-ter)] truncate max-w-sm">{kernel.description}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <StrategyConnectivityHelpButton context="kernel_detail" className="border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:bg-[var(--bg-raised)]" />
          {kernel.status === 'draft' && (
            <button
              onClick={() => activateKernel(kernel.id)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green)]/20 px-3 py-1.5 transition-colors"
            >
              <CheckIcon className="w-3.5 h-3.5" />
              Activate
            </button>
          )}
          {kernel.status === 'active' && (
            <button
              onClick={() => archiveKernel(kernel.id)}
              className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] border border-[var(--rule)] hover:bg-[var(--bg-raised)] px-3 py-1.5 transition-colors"
            >
              <ArchiveBoxIcon className="w-3.5 h-3.5" />
              Archive
            </button>
          )}
          {kernel.status === 'archived' && (
            <button
              onClick={() => updateKernel(kernel.id, { status: 'draft' })}
              className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] border border-[var(--rule)] hover:bg-[var(--bg-raised)] px-3 py-1.5 transition-colors"
            >
              Restore
            </button>
          )}
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 text-sm text-[var(--coral)] border border-[var(--coral)]/50 hover:bg-[var(--coral)]/20 px-3 py-1.5 transition-colors"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────── */}
      <div className="border-b border-[var(--rule)]">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  active
                    ? 'border-[var(--mustard)] text-[var(--mustard)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:border-[var(--rule)]'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Tab description ───────────────────────────────────────── */}
      <p className="text-xs text-[var(--text-ter)] -mt-4">
        {TABS.find(t => t.id === activeTab)?.description}
      </p>

      {/* ── Tab content ───────────────────────────────────────────── */}
      <div className="min-h-64">
        {activeTab === 'kernel'      && <KernelForm kernel={kernel} />}
        {activeTab === 'challenges'  && <ChallengesTab kernelId={kernel.id} />}
        {activeTab === 'objectives'  && <ProximateObjectivesTree kernelId={kernel.id} />}
        {activeTab === 'takes'       && <TakeGrid kernelId={kernel.id} />}
        {activeTab === 'assumptions' && <AssumptionTracker kernelId={kernel.id} />}
      </div>
    </div>
  );
}

export default KernelDetail;
